import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { FiPlus } from 'react-icons/fi';
import { MdOutlineEdit, MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import DataTable from 'components/Common/DataTable';
import Button from 'components/Common/Button';
import Pagination from '@/components/Common/Pagination';
import PromptBuilderEmptyStateIcon from '@/assets/icons/PromptBuilderEmptyStateIcon';
import { useDeleteHostSms, useGetHostSmsList } from '@/apis/business/setting/phoneNumbers/hook';
import { HostSms } from '@/apis/business/setting/phoneNumbers/types';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { confirmDialog } from 'utils/helperFunctions';
import HostSmsDrawer from './HostSmsDrawer';
import HostSmsStatusDrawer from './HostSmsStatusDrawer';
import {
  HOST_SMS_POLL_INTERVAL_MS,
  IN_PROGRESS_STATUSES,
  formatAddedOn,
  formatHostSmsNumber,
  providerLabel,
  statusPresentation
} from './hostSmsConstants';

type Props = {
  /** When true, omit the standalone page title (card chrome lives in Telephony Add-Ons). */
  embedded?: boolean;
  preferredEndpoint?: TelephonyEndpoint | null;
  onEmptyChange?: (empty: boolean) => void;
};

export default function HostSmsTab({ embedded = false, preferredEndpoint = null, onEmptyChange }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<HostSms | null>(null);
  const [statusRow, setStatusRow] = useState<HostSms | null>(null);
  const [prefillingEndpoint, setPrefillingEndpoint] = useState<TelephonyEndpoint | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [preferredEndpoint?.id]);

  const listParams = useMemo(() => {
    const params: Record<string, unknown> = { page: currentPage, per_page: perPage };
    if (preferredEndpoint?.id) {
      params.telephony_endpoint_id = preferredEndpoint.id;
    } else if (preferredEndpoint?.phone_number) {
      params.number = preferredEndpoint.phone_number;
    }
    return params;
  }, [currentPage, perPage, preferredEndpoint?.id, preferredEndpoint?.phone_number]);

  const { data, isLoading } = useGetHostSmsList(listParams, {
    refetchInterval: (query) =>
      query.state.data?.data?.some((row) => IN_PROGRESS_STATUSES.includes(row.status))
        ? HOST_SMS_POLL_INTERVAL_MS
        : false
  });
  const rows = useMemo(() => data?.data ?? [], [data]);
  // When scoped to a number, only that number's Host SMS rows are returned.
  const hostSmsCount = data?.meta?.total_count ?? rows.length;
  const canAddHostSms = hostSmsCount < 1;
  const isEmpty = !isLoading && rows.length === 0;
  const deleteHostSms = useDeleteHostSms();

  useEffect(() => {
    onEmptyChange?.(isEmpty);
  }, [isEmpty, onEmptyChange]);

  const openNewDrawer = () => {
    setEditingRow(null);
    setPrefillingEndpoint(preferredEndpoint);
    setDrawerOpen(true);
  };

  const openEditDrawer = useCallback((row: HostSms) => {
    setStatusRow(null);
    setEditingRow(row);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setPrefillingEndpoint(null);
    setDrawerOpen(false);
    setEditingRow(null);
  };

  const handleSubmit = (row: HostSms) => {
    closeDrawer();
    setStatusRow(row);
  };

  const handleDelete = useCallback(
    async (row: HostSms) => {
      const hasOrder = Boolean(row.order_details?.order_id) && row.status !== 'failed';
      const result = await confirmDialog({
        title: hasOrder ? 'Remove hosted messaging?' : 'Delete number?',
        text: hasOrder
          ? `SMS hosting will be disconnected from ${formatHostSmsNumber(row.number)}. Your voice service is not affected.`
          : `Remove ${formatHostSmsNumber(row.number)} from Host SMS?`,
        icon: 'warning',
        iconColor: '#DA0808',
        confirmButtonColor: '#DA0808',
        confirmButtonText: hasOrder ? 'Disconnect' : 'Delete'
      });
      if (!result.isConfirmed) return;
      deleteHostSms.mutate(row.id);
    },
    [deleteHostSms]
  );

  const columns = useMemo<ColumnDef<HostSms>[]>(
    () => [
      {
        header: 'NUMBER',
        accessorKey: 'number',
        cell: ({ row }) => formatHostSmsNumber(row.original.number)
      },
      {
        header: 'PROVIDER',
        accessorKey: 'provider',
        cell: ({ row }) => providerLabel(row.original.provider)
      },
      {
        header: 'CAMPAIGN',
        accessorKey: 'campaign_name',
        cell: ({ row }) => row.original.campaign_name || row.original.campaign_id || '-'
      },
      {
        header: 'ADDED ON',
        accessorKey: 'created_at',
        cell: ({ row }) => formatAddedOn(row.original.created_at)
      },
      {
        header: 'STATUS',
        accessorKey: 'status',
        cell: ({ row }) => {
          const presentation = statusPresentation(row.original.status);
          return (
            <Chip
              label={presentation.label}
              size='small'
              sx={{
                bgcolor: presentation.bg,
                color: presentation.color,
                fontWeight: 500,
                fontSize: 12,
                borderRadius: '999px',
                height: 24,
                px: 0.5
              }}
            />
          );
        }
      },
      {
        header: 'ACTIONS',
        id: 'actions',
        cell: ({ row }) => {
          const { editable, status } = row.original;
          const deletable = status !== 'disconnecting' && status !== 'disconnected';
          const formOpenable = status !== 'disconnecting' && status !== 'disconnected';
          return (
            <Box display='flex' alignItems='center'>
              <Tooltip title={editable ? '' : 'View'} placement='top'>
                <span>
                  <IconButton aria-label='View' disabled={editable} onClick={() => setStatusRow(row.original)}>
                    <MdOutlineRemoveRedEye />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={formOpenable ? (editable ? 'Edit' : 'Replace LOA') : ''} placement='top'>
                <span>
                  <IconButton aria-label='Edit' disabled={!formOpenable} onClick={() => openEditDrawer(row.original)}>
                    <MdOutlineEdit />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={deletable ? 'Delete' : ''} placement='top'>
                <span>
                  <IconButton
                    aria-label='Delete'
                    disabled={!deletable || deleteHostSms.isPending}
                    onClick={() => handleDelete(row.original)}
                    color='error'
                  >
                    <RiDeleteBin6Line style={{ color: deletable ? 'red' : undefined }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
      }
    ],
    [handleDelete, openEditDrawer, deleteHostSms.isPending]
  );

  return (
    <Box>
      {embedded && isEmpty ? (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          textAlign='center'
          py={4}
          px={2}
          border='1px solid #E5E7EB'
          borderRadius='8px'
        >
          <Box sx={{ '& svg': { width: 140, height: 'auto' }, opacity: 0.85 }}>
            <PromptBuilderEmptyStateIcon />
          </Box>
          <Typography fontSize={13} color='#6B7280' maxWidth={420} lineHeight={1.6} mt={1.5} mb={2.5}>
            You haven&apos;t hosted any existing numbers yet. Host a number to enable SMS on it.
          </Typography>
          <Button
            variant='contained'
            color='primary'
            startIcon={<FiPlus size={16} />}
            onClick={openNewDrawer}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', borderRadius: '999px', px: 2.5 }}
          >
            Host SMS
          </Button>
        </Box>
      ) : (
        <>
          {(canAddHostSms || !embedded) && (
            <Box
              display='flex'
              flexDirection={{ xs: 'column', sm: 'row' }}
              justifyContent={embedded ? 'flex-end' : 'space-between'}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              gap={2}
              mb={3}
            >
              {!embedded && (
                <Box>
                  <Typography variant='h6' fontWeight={500}>
                    Host SMS
                  </Typography>
                  <Typography fontSize={14} color='text.secondary' mt={0.5}>
                    Activate SMS on your existing business number—no need to change your current provider.
                  </Typography>
                </Box>
              )}
              {canAddHostSms && (
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<FiPlus size={16} />}
                  onClick={openNewDrawer}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    borderRadius: '999px',
                    px: 2.5
                  }}
                >
                  Host SMS
                </Button>
              )}
            </Box>
          )}

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            isHeaderSticky={false}
            enableColumnResizing={false}
            containerStyles={{ borderBottom: '1px solid #ECECEC', overflowX: 'auto' }}
          />

          <Pagination
            totalPages={data?.meta?.total_pages}
            totalCount={data?.meta?.total_count ?? 0}
            currentPage={currentPage}
            perPage={perPage}
            onChange={setCurrentPage}
            onPerPageChange={setPerPage}
          />
        </>
      )}

      <HostSmsDrawer
        open={drawerOpen}
        record={editingRow}
        telephonyEndpoint={prefillingEndpoint}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
      />
      <HostSmsStatusDrawer
        open={Boolean(statusRow)}
        record={statusRow}
        onClose={() => setStatusRow(null)}
        onEdit={openEditDrawer}
      />
    </Box>
  );
}
